import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "@progress/kendo-angular-dialog";
import { map, Observable, Subscription } from "rxjs";
import { isiFirmTax, isTaxprep, isJobsBilling } from "src/app/views-interfaces/ApplicationType.enum";
import { CardCategoryDisplayPreference, DisplayCardBasedOn } from "src/app/views-interfaces/dashboard-model.interface";
import {
    AnalyticsService,
    AuthService,
    CardViewService,
    DashboardsService,
    HelpersService,
    ResponseErrorHandlerService,
    SettingsBreadcrumbService,
    UsersService,
} from "../../shared";
import type { DashboardSelectCanChangeArgs } from "@shared/dashboard-select";
import { FirmsService } from "../../shared/firms-service/firms.service";
import { UtilsService } from "../../shared/utils/utils.service";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { CancelConfirmationComponent } from "../settings-dashboard-management-component/cancel-confirmation/cancel-confirmation.component";

@Component({
    selector: "wk-settings-general",
    templateUrl: "./settings-general.component.html",
    styleUrl: "./settings-general.component.scss",
    standalone: false,
    host: {
        class: "wk-sg",
        "cg3-theme": "PDS-v3",
    },
})
export class SettingsGeneralComponent implements OnInit, OnDestroy {
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly fb = inject(UntypedFormBuilder);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly dialogService = inject(DialogService);
    private readonly translate = inject(TranslateService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly breadcrumbService = inject(SettingsBreadcrumbService);
    private readonly router = inject(Router);
    authService = inject(AuthService);
    firmsService = inject(FirmsService);
    usersService = inject(UsersService);

    subscription: Subscription;
    groupSettingForm: UntypedFormGroup = null;
    loading = false;
    currentDashboard: IDashboardModel;
    public isTaxprepDashboard: boolean;
    public isiFirmDashboard: boolean;
    public isJobsBillingDashboard: boolean;
    DisplayCardBasedOnValues = DisplayCardBasedOn;
    CardCategoryDisplayPreferenceValues = CardCategoryDisplayPreference;

    constructor() {
        this.subscription = this.dashboardsService.currentDashboardChanged$.subscribe(() => {
            this.init();
        });
    }

    async ngOnInit() {
        this.breadcrumbService.setBreadcrumbs("general");
        this.analyticsService.track("Access General");

        this.groupSettingForm = this.fb.group({
            Id: null,
            ClientCode: "clientCodeId",
            DaysBeforeDeadlineAlert: 0,
            DaysBeforeDeadlineWarning: 0,
            DaysInStepAlert: 0,
            DaysInStepWarning: 0,
            DisplayGroupedCards: true,
            DisplayCardBasedOn: true,
            ReturnStatusDefaultValue: false,
            AssignedStatusDefaultValue: false,
            AutomaticSyncOpen: false,
            AutomaticSyncClose: false,
            CardCategoryDisplayPreference: true,
        });

        await this.init();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isTaxprepDashboard = isTaxprep(this.currentDashboard?.ApplicationType);
        this.isiFirmDashboard = isiFirmTax(this.currentDashboard?.ApplicationType);
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard?.ApplicationType);
        this.groupSettingForm = this.fb.group({
            ClientCode: this.isiFirmDashboard === true ? "clientCodeiFirm" : this.currentDashboard.ClientCode,
            DaysBeforeDeadlineAlert: new UntypedFormControl(this.currentDashboard.DaysBeforeDeadlineAlert, [
                Validators.required,
                this.deadlineValidator,
            ]),
            DaysBeforeDeadlineWarning: new UntypedFormControl(this.currentDashboard.DaysBeforeDeadlineWarning, [
                Validators.required,
                this.deadlineValidator,
            ]),
            DaysInStepAlert: new UntypedFormControl(this.currentDashboard.DaysInStepAlert, [Validators.required, this.sameStepValidator]),
            DaysInStepWarning: new UntypedFormControl(this.currentDashboard.DaysInStepWarning, [Validators.required, this.sameStepValidator]),
            DisplayGroupedCards: this.currentDashboard.DisplayGroupedCards,
            DisplayCardBasedOn: this.currentDashboard.DisplayCardBasedOn,
            ReturnStatusDefaultValue: this.currentDashboard.ReturnStatusDefaultValue,
            AssignedStatusDefaultValue: this.currentDashboard.AssignedStatusDefaultValue,
            AutomaticSyncOpen: this.currentDashboard.AutomaticSyncOpen,
            AutomaticSyncClose: this.currentDashboard.AutomaticSyncClose,
            CardCategoryDisplayPreference: this.currentDashboard.CardCategoryDisplayPreference,
        });
        this.helpersService.suppressSpinner();
    }

    deadlineValidator(input: UntypedFormControl) {
        if (input?.root["controls"]) {
            const daysBeforeDeadlineAlert = input.root["controls"].DaysBeforeDeadlineAlert;
            const daysBeforeDeadlineWarning = input.root["controls"].DaysBeforeDeadlineWarning;
            const isValid = daysBeforeDeadlineWarning.value > daysBeforeDeadlineAlert.value;

            if (!isValid || daysBeforeDeadlineAlert.value == null || daysBeforeDeadlineWarning.value == null) {
                daysBeforeDeadlineAlert.setErrors({ error: "DaysInStepWarning" });
                daysBeforeDeadlineWarning.setErrors({ error: "DaysInStepWarning" });
            } else {
                daysBeforeDeadlineAlert.setErrors(null);
                daysBeforeDeadlineWarning.setErrors(null);
            }

            return isValid ? null : { error: "DaysInStepWarning" };
        } else {
            return null;
        }
    }

    sameStepValidator(input: UntypedFormControl) {
        if (input?.root["controls"]) {
            const daysInStepAlert = input.root["controls"].DaysInStepAlert;
            const daysInStepWarning = input.root["controls"].DaysInStepWarning;
            const isValid = daysInStepAlert.value >= daysInStepWarning.value;

            if (!isValid || daysInStepAlert.value == null || daysInStepWarning.value == null) {
                daysInStepAlert.setErrors({ error: "DaysInStepAlert" });
                daysInStepWarning.setErrors({ error: "DaysInStepAlert" });
            } else {
                daysInStepAlert.setErrors(null);
                daysInStepWarning.setErrors(null);
            }

            return isValid ? null : { error: "DaysInStepAlert" };
        } else {
            return null;
        }
    }

    numberInput(e: Event) {
        UtilsService.enforceMinMax(e.target);
    }

    checkValidation() {
        this.groupSettingForm.root["controls"].DaysBeforeDeadlineAlert.updateValueAndValidity();
        this.groupSettingForm.root["controls"].DaysBeforeDeadlineWarning.updateValueAndValidity();
        this.groupSettingForm.root["controls"].DaysInStepAlert.updateValueAndValidity();
        this.groupSettingForm.root["controls"].DaysInStepWarning.updateValueAndValidity();
    }

    submitForm(e) {
        e.preventDefault();
        if (this.groupSettingForm.valid && !this.loading) {
            this.sendAnalitics();
            this.loading = true;
            this.dashboardsService.updateDashboard(this.currentDashboard.Id, this.groupSettingForm.value).subscribe({
                next: async (response) => {
                    this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
                    const analyticsPayload: any = {};

                    if (this.currentDashboard.DaysInStepAlert !== this.groupSettingForm.value.DaysInStepAlert) {
                        analyticsPayload.alertDaysInTheSteps = Number(this.groupSettingForm.value.DaysInStepAlert);
                    }

                    if (this.currentDashboard.DaysInStepWarning !== this.groupSettingForm.value.DaysInStepWarning) {
                        analyticsPayload.warningDaysInTheSteps = Number(this.groupSettingForm.value.DaysInStepWarning);
                    }

                    if (this.currentDashboard.DaysBeforeDeadlineAlert !== this.groupSettingForm.value.DaysBeforeDeadlineAlert) {
                        if (this.isJobsBillingDashboard) {
                            analyticsPayload.alertDaysBeforeTargetEndDate = Number(this.groupSettingForm.value.DaysBeforeDeadlineAlert);
                        } else {
                            analyticsPayload.alertDaysBeforeDeadline = Number(this.groupSettingForm.value.DaysBeforeDeadlineAlert);
                        }
                    }

                    if (this.currentDashboard.DaysBeforeDeadlineWarning !== this.groupSettingForm.value.DaysBeforeDeadlineWarning) {
                        if (this.isJobsBillingDashboard) {
                            analyticsPayload.warningDaysBeforeTargetEndDate = Number(this.groupSettingForm.value.DaysBeforeDeadlineWarning);
                        } else {
                            analyticsPayload.warningDaysBeforeDeadline = Number(this.groupSettingForm.value.DaysBeforeDeadlineWarning);
                        }
                    }

                    this.analyticsService.track("Save General", analyticsPayload);
                    if (response?.Dashboard.Id) {
                        this.dashboardsService.refresh(true);
                    }
                    this.groupSettingForm.markAsPristine();
                    this.responseErrorHandlerService.showPopup({
                        typeLayout: "SUCCESS",
                        messageCode: "successfullySaved",
                    });
                    this.cardViewService.settingsSectionChanged = true;
                },
                error: () => {
                    this.responseErrorHandlerService.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "CancelSaved",
                    });
                },
                complete: () => {
                    this.loading = false;
                },
            });
        }
    }

    cancelForm() {
        if (this.groupSettingForm.dirty && !this.loading) {
            this.openPendingChangesDialogConfirmation().subscribe(async (result) => {
                if (result == 1) {
                    this.router.navigate(["/settings/main"]);
                } else if (result == 2) {
                    return;
                }
            });
        } else {
            this.router.navigate(["/settings/main"]);
        }
    }

    onBreadcrumbNavigate(event: { route: string; proceed: () => void }): void {
        if (this.groupSettingForm.dirty && !this.loading) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result === 1) {
                    this.groupSettingForm.markAsPristine();
                    event.proceed();
                }
            });
        } else {
            event.proceed();
        }
    }

    sendAnalitics() {
        if (this.currentDashboard.ClientCode !== this.groupSettingForm.value.ClientCode) {
            this.analyticsService.track("User changes Client code", {
                "Client code": this.groupSettingForm.value.ClientCode,
            });
        }
        if (this.currentDashboard.DisplayGroupedCards !== this.groupSettingForm.value.DisplayGroupedCards) {
            this.analyticsService.track("Users change Display card settings", {
                "Display card": this.groupSettingForm.value.DisplayGroupedCards === true ? "Client file" : "Taxpayer",
            });
        }
        if (this.currentDashboard.DisplayCardBasedOn !== this.groupSettingForm.value.DisplayCardBasedOn) {
            this.analyticsService.track("Users change Display card settings", {
                "Display card": this.groupSettingForm.value.DisplayCardBasedOn === true ? "Client file" : "Taxpayer",
            });
        }
    }

    openPendingChangesDialogConfirmation(): Observable<number> {
        const dialog = this.dialogService.open({
            title: this.translate.instant("AccessPendingChanges.Title"),
            content: CancelConfirmationComponent,
            actions: [
                { text: this.translate.instant("General.Discard"), primary: true, choice: 1 },
                { text: this.translate.instant("General.Cancel"), choice: 2 },
            ],
            width: 600,
            cssClass: "access-pending-changes-dialog",
        });
        return dialog.result.pipe(
            map((result: any) => {
                return result.choice;
            }),
        );
    }

    canChangeDashboard(args: DashboardSelectCanChangeArgs) {
        if (this.groupSettingForm.dirty && !this.loading) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result <= 2) {
                    if (result === 1) {
                        this.groupSettingForm.markAsPristine();
                        if (args) {
                            this.doChangeDashboard(args);
                        }
                    } else if (args) {
                        args.cancelAction();
                    }
                } else if (args) {
                    args.cancelAction();
                }
            });
        } else {
            this.doChangeDashboard(args);
        }
    }

    doChangeDashboard(args) {
        args.action(args.dashboardId);
    }
}
